import axios from 'axios';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const api = axios.create({
  baseURL: API,
});


api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


export const authAPI = {
  sendOTP: (phone_number) => api.post('/auth/send-otp', { phone_number }),
  verifyOTP: (phone_number, otp) => api.post('/auth/verify-otp', { phone_number, otp }),
  getMe: () => api.get('/auth/me'),
};


export const productAPI = {
  getAll: (opts = {}) => api.get('/products', { params: opts }),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  setDefaultDiscount: (pct) => api.post('/products/set-default-discount', { discount_percent: pct }),
  // Find-or-create a virtual product for the (parent, sub, weight, variant) tuple.
  // Idempotent: same input always returns the same product record. Per 21/Feb/2026 spec.
  resolveVirtual: (data) => api.post('/products/resolve-virtual', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/product-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};


export const subBrandAPI = {
  getAll: () => api.get('/sub-brands'),
  create: (name) => api.post('/sub-brands', { name }),
  delete: (id) => api.delete(`/sub-brands/${id}`),
};


export const orderAPI = {
  getAll: (status = null) => api.get('/orders', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  adminCreate: (data) => api.post('/orders/admin-create', data),
  confirm: (id, data) => api.put(`/orders/${id}/confirm`, data),
  dispatch: (id, data) => api.put(`/orders/${id}/dispatch`, data),
  dispatchSplit: (id, data) => api.post(`/orders/${id}/dispatch-split`, data),
  getRemaining: (id) => api.get(`/orders/${id}/remaining`),
  listDispatches: (params) => api.get('/dispatches', { params }),
  markDelivered: (order_id, dispatch_id, body = null) => api.post(`/orders/${order_id}/dispatches/${dispatch_id}/deliver`, body || {}),
  revertDelivery: (order_id, dispatch_id) => api.post(`/orders/${order_id}/dispatches/${dispatch_id}/revert-delivery`),
  deleteDispatch: (order_id, dispatch_id) => api.delete(`/orders/${order_id}/dispatches/${dispatch_id}`),
  updateDispatchTransport: (order_id, dispatch_id, body) => api.put(`/orders/${order_id}/dispatches/${dispatch_id}/transport`, body),
  deliver: (id) => api.put(`/orders/${id}/deliver`),
  getInvoice: (id) => api.get(`/orders/${id}/invoice`),
  delete: (id) => api.delete(`/orders/${id}`),
  updatePayment: (id, data) => api.put(`/orders/${id}/payment`, data),
  edit: (id, data) => api.put(`/orders/${id}/edit`, data),
  reschedule: (id, data) => api.put(`/orders/${id}/reschedule`, data),
  bulkReschedule: (data) => api.put('/orders/bulk-reschedule', data),
  bulkConfirm: (data) => api.put('/orders/bulk-confirm', data),
  toggleHold: (id) => api.put(`/orders/${id}/toggle-hold`),
  updateManualAmount: (id, data) => api.put(`/orders/${id}/manual-amount`, data),
  updateManualDescription: (id, data) => api.put(`/orders/${id}/manual-description`, data),
  revert: (id, data) => api.put(`/orders/${id}/revert`, data),
};


export const transporterAPI = {
  list: () => api.get('/transporters'),
  create: (data) => api.post('/transporters', data),
  update: (id, data) => api.put(`/transporters/${id}`, data),
  remove: (id) => api.delete(`/transporters/${id}`),
  addVehicle: (id, data) => api.post(`/transporters/${id}/vehicles`, data),
  removeVehicle: (id, vid) => api.delete(`/transporters/${id}/vehicles/${vid}`),
  addDriver: (id, data) => api.post(`/transporters/${id}/drivers`, data),
  removeDriver: (id, did) => api.delete(`/transporters/${id}/drivers/${did}`),
};


export const dealerAPI = {
  getAll: () => api.get('/dealers'),
  create: (data) => api.post('/dealers', data),
  update: (id, data) => api.put(`/dealers/${id}`, data),
  delete: (id) => api.delete(`/dealers/${id}`),
  importExcel: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/dealers/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  downloadMobileTemplate: () => api.get('/dealers/mobile-template', { responseType: 'blob' }),
  uploadMobileUpdate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/dealers/mobile-update', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  markBillingReminder: (id) => api.post(`/dealers/${id}/billing-reminder`),
};


export const crmAPI = {
  uploadOutstandings: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/crm/outstandings/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getMeta: () => api.get('/crm/outstandings/meta'),
  getDealers: () => api.get('/crm/dealers'),
  getDealerDetail: (id) => api.get(`/crm/dealers/${id}/detail`),
  setCallMark: (id, body) => api.put(`/crm/dealers/${id}/call-mark`, body),
  setCrmCallMark: (id, body) => api.put(`/crm/dealers/${id}/crm-call-mark`, body),
  exportExcel: (body) => api.post('/crm/export/excel', body, { responseType: 'blob' }),
  exportPdf: (body) => api.post('/crm/export/pdf', body, { responseType: 'blob' }),
  eraseAllOutstandings: () => api.delete('/crm/outstandings/all'),
};


// Admin-editable runtime settings (billing reminder template, etc.).
// Keeps the WhatsApp message text out of the codebase so non-technical
// admins can adjust greetings without a redeploy.
export const settingsAPI = {
  getBillingTemplate: () => api.get('/settings/billing-reminder-template'),
  updateBillingTemplate: (template) => api.put('/settings/billing-reminder-template', { template }),
  resetBillingTemplate: () => api.delete('/settings/billing-reminder-template'),
};


export const stockAPI = {
  getMatrix: () => api.get('/stock'),
  getDaily: (date_from, date_to) => api.get('/stock/daily', { params: { date_from, date_to } }),
  adjust: (facility, product_id, delta, reason) => api.post('/stock/adjust', { facility, product_id, delta, reason }),
  history: (params) => api.get('/stock/history', { params }),
};


export const freightAPI = {
  // Master rate card CRUD
  listMaster: () => api.get('/freight/master'),
  createMaster: (body) => api.post('/freight/master', body),
  updateMaster: (id, body) => api.put(`/freight/master/${id}`, body),
  deleteMaster: (id) => api.delete(`/freight/master/${id}`),
  listLocations: () => api.get('/freight/locations'),
  migrateMaster: () => api.post('/freight/migrate-master-schema'),
  uploadMaster: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/freight/master/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Dealer-specific split overrides
  listDealerOverrides: () => api.get('/freight/dealer-overrides'),
  upsertDealerOverride: (body) => api.post('/freight/dealer-overrides', body),
  deleteDealerOverride: (id) => api.delete(`/freight/dealer-overrides/${id}`),
  // Freight lines (auto-spawned per dispatch)
  listLines: (params) => api.get('/freight/lines', { params }),
  orderEstimates: () => api.get('/freight/order-estimates'),
  orderEstimate: (orderId) => api.get(`/freight/order-estimate/${orderId}`),
  updateLine: (id, body) => api.put(`/freight/lines/${id}`, body),
  recomputeLine: (id) => api.post(`/freight/lines/${id}/recompute`),
  resyncAll: () => api.post('/freight/resync'),
  // Bills (groups of lines for a transporter × period)
  listBills: (params) => api.get('/freight/bills', { params }),
  getBill: (id) => api.get(`/freight/bills/${id}`),
  createBill: (body) => api.post('/freight/bills', body),
  updateBillStatus: (id, body) => api.put(`/freight/bills/${id}/status`, body),
  deleteBill: (id) => api.delete(`/freight/bills/${id}`),
  // Downloadable exports — return Blob via responseType
  excelUrl: (id) => `/freight/bills/${id}/excel`,
  pdfUrl: (id) => `/freight/bills/${id}/pdf`,
  // Lines list export (same filter params as listLines)
  exportLinesExcel: (params) => api.get('/freight/lines/export/excel', { params, responseType: 'blob' }),
  exportLinesPdf: (params) => api.get('/freight/lines/export/pdf', { params, responseType: 'blob' }),
};


export const productionAPI = {
  getSheets: (date) => api.get('/production/sheets', { params: { date } }),
  activateSheet: (date, facility) => api.post('/production/sheets/activate', { date, facility }),
  closeSheet: (sheet_id) => api.post(`/production/sheets/${sheet_id}/close`),
  updateSheetNotes: (sheet_id, notes) => api.put(`/production/sheets/${sheet_id}/notes`, { notes }),
  recentSheets: (limit = 30) => api.get('/production/sheets/recent', { params: { limit } }),
  createEntry: (body) => api.post('/production', body),
  updateEntry: (id, body) => api.put(`/production/${id}`, body),
  deleteEntry: (id) => api.delete(`/production/${id}`),
  getPlan: (params) => api.get('/production/plan', { params }),
  // Lot sheets — new planner replacement
  syncLotSheets: (body) => api.post('/production/lot-sheets/sync', body),
  listLotSheets: (params) => api.get('/production/lot-sheets', { params }),
  getLotSheet: (id) => api.get(`/production/lot-sheets/${id}`),
  createLotSheet: (body) => api.post('/production/lot-sheets', body),
  updateLotSheet: (id, body) => api.put(`/production/lot-sheets/${id}`, body),
  updateLotSheetSeq: (id, seq_no) => api.put(`/production/lot-sheets/${id}/seq`, { seq_no }),
  syncLotSheetToStock: (id) => api.post(`/production/lot-sheets/${id}/sync-to-stock`),
  closeLotSheet: (id) => api.post(`/production/lot-sheets/${id}/close`),
  reopenLotSheet: (id) => api.post(`/production/lot-sheets/${id}/reopen`),
  deleteLotSheet: (id) => api.delete(`/production/lot-sheets/${id}`),
  // Plan overrides (persisted Produce bag inputs per date × product)
  getPlanOverrides: (date) => api.get('/production/plan/overrides', { params: { date } }),
  savePlanOverride: (body) => api.put('/production/plan/overrides', body),
  deletePlanOverride: (date, product_id) => api.delete('/production/plan/overrides', { params: { date, product_id } }),
};


export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getDealerReports: () => api.get('/analytics/dealer-reports'),
  getReceivables: () => api.get('/analytics/receivables'),
  getProductSummary: (params) => api.get('/analytics/product-summary', { params }),
  getInTransitAudit: () => api.get('/admin/in-transit-audit'),
};


export const exportAPI = {
  downloadExcel: () => `${API}/export/dealer-reports/excel`,
  downloadPDF: () => `${API}/export/dealer-reports/pdf`,
  ordersExcelUrl: (params) => {
    const q = new URLSearchParams(params).toString();
    return `${API}/export/orders/excel${q ? '?' + q : ''}`;
  },
  ordersPDFUrl: (params) => {
    const q = new URLSearchParams(params).toString();
    return `${API}/export/orders/pdf${q ? '?' + q : ''}`;
  },
  deliveredStackExcel: (params = {}) => api.get('/export/delivered-stack/excel', { params, responseType: 'blob' }),
  deliveredStackPDF: (params = {}) => api.get('/export/delivered-stack/pdf', { params, responseType: 'blob' }),
  stockExcel: (params = {}) => api.get('/export/stock/excel', { params, responseType: 'blob' }),
  stockPDF: (params = {}) => api.get('/export/stock/pdf', { params, responseType: 'blob' }),
};


// Staff user management — admin-only (server enforces require_strict_admin).
export const userAPI = {
  listStaff: () => api.get('/users/staff'),
  createStaff: (body) => api.post('/users/staff', body),
  updateStaff: (id, body) => api.put(`/users/staff/${id}`, body),
  deleteStaff: (id) => api.delete(`/users/staff/${id}`),
  impersonate: (id) => api.post(`/users/staff/${id}/impersonate`),
};


// Product Costing — RM Master (admin-only; server enforces require_strict_admin).
export const costingAPI = {
  listCategories: () => api.get('/costing/categories'),
  createCategory: (name) => api.post('/costing/categories', { name }),
  updateCategory: (id, name) => api.put(`/costing/categories/${id}`, { name }),
  deleteCategory: (id) => api.delete(`/costing/categories/${id}`),
  listRawMaterials: (category) => api.get('/costing/raw-materials', { params: category ? { category } : {} }),
  createRawMaterial: (body) => api.post('/costing/raw-materials', body),
  updateRawMaterial: (id, body) => api.put(`/costing/raw-materials/${id}`, body),
  deleteRawMaterial: (id) => api.delete(`/costing/raw-materials/${id}`),
  rateHistory: (id) => api.get(`/costing/raw-materials/${id}/rate-history`),
  rateTrends: () => api.get('/costing/raw-materials/trends'),
  // Overhead heads (admin-manageable)
  listOverheads: () => api.get('/costing/overheads'),
  createOverhead: (body) => api.post('/costing/overheads', body),
  updateOverhead: (id, body) => api.put(`/costing/overheads/${id}`, body),
  deleteOverhead: (id) => api.delete(`/costing/overheads/${id}`),
  // Formulations (one per finished product)
  listFormulations: () => api.get('/costing/formulations'),
  getFormulationByProduct: (pid) => api.get(`/costing/formulations/by-product/${pid}`),
  listFormulationVersions: (pid) => api.get(`/costing/formulations/${pid}/versions`),
  upsertFormulation: (body) => api.post('/costing/formulations', body),
  deleteFormulation: (id) => api.delete(`/costing/formulations/${id}`),
  // Cost sheet (computed)
  getCostSheet: (pid, params) => api.get(`/costing/cost-sheet/${pid}`, { params }),
  listCostSheets: (params) => api.get('/costing/cost-sheets', { params }),
  // Cost snapshots (dated summaries)
  listSnapshots: () => api.get('/costing/snapshots'),
  createSnapshot: (body) => api.post('/costing/snapshots', body),
  deleteSnapshot: (id) => api.delete(`/costing/snapshots/${id}`),
  exportCostSummaryExcel: (params) => api.get('/costing/export/cost-summary/excel', { params, responseType: 'blob' }),
  exportCostSummaryPdf: (params) => api.get('/costing/export/cost-summary/pdf', { params, responseType: 'blob' }),
  // Exports (blob)
  exportRmMasterExcel: () => api.get('/costing/export/rm-master/excel', { responseType: 'blob' }),
  exportRmMasterPdf: () => api.get('/costing/export/rm-master/pdf', { responseType: 'blob' }),
  exportCostSheetExcel: (pid, params) => api.get(`/costing/export/cost-sheet/${pid}/excel`, { params, responseType: 'blob' }),
  exportCostSheetPdf: (pid, params) => api.get(`/costing/export/cost-sheet/${pid}/pdf`, { params, responseType: 'blob' }),
  exportComparisonExcel: (params) => api.get('/costing/export/comparison/excel', { params, responseType: 'blob' }),
  exportComparisonPdf: (params) => api.get('/costing/export/comparison/pdf', { params, responseType: 'blob' }),
};


export const getFileUrl = (storagePath) => {
  const token = localStorage.getItem('token');
  return `${API}/files/${storagePath}?auth=${token}`;
};


export default api;
